import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

const ProjectsPage = () => {
  // SEO Configuration
  useSEO({
    title: 'Project Portfolio - Fintech & Banking Software Projects | PURECODE Warrington',
    description: 'View PURECODE\'s successful project portfolio: lending platforms, mobile banking apps, payment gateways, analytics dashboards, and more. 50+ completed projects for fintech startups and financial services in Warrington and UK.',
    keywords: 'fintech projects, banking software, project portfolio, software development examples, Warrington IT projects, case studies',
    canonical: 'https://purecode.uk/projects',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Projects', url: '/projects' }
        ]),
        {
          '@type': 'CollectionPage',
          '@id': 'https://purecode.uk/projects#collectionpage',
          url: 'https://purecode.uk/projects',
          name: 'Project Portfolio',
          description: 'Successful fintech and banking software projects'
        }
      ]
    }
  });

  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', name: 'All Projects' },
    { id: 'fintech', name: 'Fintech Startups' },
    { id: 'betting', name: 'Gaming & Betting' },
    { id: 'finance', name: 'Financial Services' },
    { id: 'ecommerce', name: 'E-commerce' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Customer Management Platform',
      client: 'FinanceFirst Solutions',
      category: 'finance',
      tags: ['CRM', 'Customer Analytics', 'Automation'],
      description: 'Built a comprehensive customer management system that streamlined client onboarding and improved customer service efficiency by 60%.',
      image: 'https://public.readdy.ai/ai/img_res/5b0471421f4689bc83b5d9977368e939.jpg',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
      duration: '4 months',
      teamSize: '3 developers',
      results: [
        '60% faster client onboarding',
        '40% improvement in customer satisfaction',
        '£50K annual operational savings',
        '99.8% system reliability'
      ]
    },
    {
      id: 2,
      title: 'Real-Time Betting Analytics',
      client: 'LocalBet Gaming',
      category: 'betting',
      tags: ['Analytics', 'Real-time Processing', 'Dashboard'],
      description: 'Developed an analytics dashboard for tracking betting patterns and user behavior, helping optimize odds and improve user engagement.',
      image: 'https://readdy.ai/api/search-image?query=Sports%20betting%20analytics%20dashboard%20with%20real-time%20data%20visualization%2C%20user%20behavior%20tracking%2C%20betting%20statistics%2C%20modern%20interface%20with%20charts%20and%20graphs&width=600&height=400&seq=project2&orientation=landscape',
      technologies: ['Vue.js', 'Node.js', 'Redis', 'MongoDB'],
      duration: '3 months',
      teamSize: '2 developers',
      results: [
        '25% increase in user engagement',
        '30% better odds optimization',
        '15% revenue growth',
        'Real-time data processing'
      ]
    },
    {
      id: 3,
      title: 'Lending Platform Development',
      client: 'TechCredit Ltd',
      category: 'fintech',
      tags: ['Lending', 'Credit Scoring', 'API Integration'],
      description: 'Created a digital lending platform with automated credit scoring and loan processing for a fintech startup.',
      image: 'https://readdy.ai/api/search-image?query=Digital%20lending%20platform%20interface%20with%20loan%20application%20forms%2C%20credit%20scoring%20dashboard%2C%20automated%20approval%20workflow%2C%20fintech%20startup%20design%20with%20modern%20UI&width=600&height=400&seq=project3&orientation=landscape',
      technologies: ['React', 'Python', 'FastAPI', 'PostgreSQL'],
      duration: '6 months',
      teamSize: '4 developers',
      results: [
        '80% faster loan processing',
        '95% automated decision making',
        '200+ loans processed monthly',
        'Regulatory compliance achieved'
      ]
    },
    {
      id: 4,
      title: 'Payment Gateway Integration',
      client: 'QuickPay Solutions',
      category: 'fintech',
      tags: ['Payments', 'API Integration', 'Security'],
      description: 'Integrated multiple payment gateways and built a unified payment processing system for a growing fintech company.',
      image: 'https://readdy.ai/api/search-image?query=Payment%20gateway%20integration%20dashboard%20with%20multiple%20payment%20methods%2C%20transaction%20monitoring%2C%20security%20features%2C%20fintech%20payment%20processing%20interface&width=600&height=400&seq=project4&orientation=landscape',
      technologies: ['Node.js', 'Stripe API', 'PayPal API', 'React'],
      duration: '2 months',
      teamSize: '2 developers',
      results: [
        '5 payment methods integrated',
        '99.9% transaction success rate',
        '50% reduction in payment failures',
        'PCI DSS compliance'
      ]
    },
    {
      id: 5,
      title: 'Investment Portfolio Tracker',
      client: 'SmartInvest Advisory',
      category: 'finance',
      tags: ['Portfolio Management', 'Data Visualization', 'Reporting'],
      description: 'Developed a portfolio tracking system that helps financial advisors monitor client investments and generate automated reports.',
      image: 'https://readdy.ai/api/search-image?query=Investment%20portfolio%20tracking%20dashboard%20with%20stock%20charts%2C%20performance%20metrics%2C%20asset%20allocation%20visualization%2C%20financial%20advisory%20software%20interface&width=600&height=400&seq=project5&orientation=landscape',
      technologies: ['React', 'D3.js', 'Node.js', 'MySQL'],
      duration: '3 months',
      teamSize: '3 developers',
      results: [
        '100+ portfolios managed',
        '70% time savings on reporting',
        '35% improvement in client satisfaction',
        'Real-time market data integration'
      ]
    },
    {
      id: 6,
      title: 'E-commerce Analytics Platform',
      client: 'RetailTech Innovations',
      category: 'ecommerce',
      tags: ['E-commerce', 'Analytics', 'Business Intelligence'],
      description: 'Built an analytics platform that tracks customer behavior, sales patterns, and inventory management for online retailers.',
      image: 'https://readdy.ai/api/search-image?query=E-commerce%20analytics%20dashboard%20with%20sales%20charts%2C%20customer%20behavior%20tracking%2C%20inventory%20management%2C%20retail%20business%20intelligence%20interface&width=600&height=400&seq=project6&orientation=landscape',
      technologies: ['Python', 'Django', 'PostgreSQL', 'Chart.js'],
      duration: '4 months',
      teamSize: '3 developers',
      results: [
        '45% increase in conversion rates',
        '30% better inventory turnover',
        '£100K additional revenue',
        'Automated reporting system'
      ]
    },
    {
      id: 7,
      title: 'Mobile Banking App',
      client: 'CommunityBank Digital',
      category: 'finance',
      tags: ['Mobile App', 'Banking', 'User Experience'],
      description: 'Developed a mobile banking application for a community bank, focusing on user-friendly design and secure transactions.',
      image: 'https://readdy.ai/api/search-image?query=Mobile%20banking%20app%20interface%20on%20smartphone%20showing%20account%20balance%2C%20transaction%20history%2C%20money%20transfer%20features%2C%20modern%20banking%20app%20design&width=600&height=400&seq=project7&orientation=landscape',
      technologies: ['React Native', 'Node.js', 'MongoDB', 'AWS'],
      duration: '5 months',
      teamSize: '4 developers',
      results: [
        '5,000+ active users',
        '90% user satisfaction rate',
        '40% reduction in branch visits',
        'Bank-grade security implemented'
      ]
    },
    {
      id: 8,
      title: 'Cryptocurrency Exchange Dashboard',
      client: 'CryptoStart Exchange',
      category: 'fintech',
      tags: ['Cryptocurrency', 'Trading', 'Real-time Data'],
      description: 'Created a trading dashboard for a small cryptocurrency exchange with real-time price feeds and order management.',
      image: 'https://readdy.ai/api/search-image?query=Cryptocurrency%20trading%20dashboard%20with%20real-time%20price%20charts%2C%20order%20books%2C%20portfolio%20management%2C%20crypto%20exchange%20interface%20with%20dark%20theme&width=600&height=400&seq=project8&orientation=landscape',
      technologies: ['React', 'WebSocket', 'Node.js', 'Redis'],
      duration: '3 months',
      teamSize: '2 developers',
      results: [
        '1,000+ daily trades',
        '99.5% uptime achieved',
        'Real-time price updates',
        'Secure wallet integration'
      ]
    }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 text-white py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(8, 145, 178, 0.85)), url('https://readdy.ai/api/search-image?query=Small%20modern%20tech%20office%20with%205%20developers%20working%20on%20computers%2C%20collaborative%20workspace%2C%20startup%20environment%2C%20financial%20technology%20development%2C%20warm%20lighting%20with%20teal%20tones&width=1920&height=600&seq=projects-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-cyan-900/70 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold tracking-tight sm:text-6xl mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-teal-300">Project Portfolio</span>
            </h1>
            <p className="text-xl leading-8 text-teal-100 max-w-3xl mx-auto">
              Discover how we've helped fintech startups, small financial services, and growing businesses 
              build innovative technology solutions that drive growth.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-gradient-to-r from-teal-50 to-cyan-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-teal-50 hover:text-teal-600 shadow-sm'
                }`}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {filteredProjects.map((project) => (
              <div key={project.id} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
                <div className="relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {project.client}
                    </span>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{project.title}</h3>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.map((tag, index) => (
                      <span key={index} className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">{project.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Duration</h4>
                      <p className="text-gray-600">{project.duration}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Team Size</h4>
                      <p className="text-gray-600">{project.teamSize}</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Results</h4>
                    <ul className="space-y-2">
                      {project.results.map((result, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-check-line text-green-500 mr-2 mt-1"></i>
                          <span className="text-gray-600">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <Link
                    to="/contact"
                    className="inline-flex items-center rounded-lg bg-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-teal-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Discuss Similar Project
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Your Next Project?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve similar results with our boutique development approach.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="rounded-lg bg-white px-8 py-4 text-lg font-semibold text-teal-600 shadow-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Start Your Project
            </Link>
            <Link
              to="/case-studies"
              className="rounded-lg border-2 border-teal-300 px-8 py-4 text-lg font-semibold text-white hover:bg-teal-500/20 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              View Case Studies
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
